import React, { FC, useMemo } from 'react';
import { LucideIcon, ArrowRightLeft, Plus, ArrowRight } from 'lucide-react';

import { ModalHeader, ModalFooter, ModalTitle } from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { StepBar } from '@epoch-components/step-bar/StepBar';
import { Toggle } from '@epoch-ui/components/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip';
import { WaitlistAdjustmentOption } from 'types/rsvp_control.types';
import { cn } from '@epoch-ui/lib/utils';

import { useGuestSideSheetContext } from '../../../../guestSideSheetContext';
import { usePlusOneRsvpControlModalContext } from '../plusOneRsvpControlContext';

interface WaitlistAdjustmentToggle {
  option: WaitlistAdjustmentOption;
  title: string;
  text: string;
  icon: LucideIcon;
  disabled?: boolean;
}

export const SetPlusOneWaitlistAdjustmentOptionContent: FC = () => {
  const {
    navigation,
    plusOneGuest: { firstName }
  } = usePlusOneRsvpControlModalContext();
  const { selectedSession } = useGuestSideSheetContext();

  const label = selectedSession ? 'session' : 'event';

  const toggles: WaitlistAdjustmentToggle[] = useMemo(
    (): WaitlistAdjustmentToggle[] => [
      {
        option: WaitlistAdjustmentOption.IncreaseCapacity,
        title: 'Increase capacity',
        text: `The capacity for the ${label} will increase by 1 to make space for ${firstName}.`,
        icon: Plus
      },
      {
        option: WaitlistAdjustmentOption.SwapWithOtherGuest,
        title: 'Swap guest',
        text: `Choose a guest marked as going to swap their spot with ${firstName}.`,
        icon: ArrowRightLeft,
        disabled: true
      }
    ],
    [firstName, label]
  );

  return (
    <>
      <ModalHeader className="tw-m-0 tw-flex tw-flex-col tw-gap-2">
        <ModalTitle className="tw-mb-0 tw-text-2xl tw-font-medium">Change RSVP status</ModalTitle>
        <span className="tw-text-primary-grey">
          This {label} is at full capacity. Increase the capacity to make updates.
        </span>
      </ModalHeader>
      <div className="tw-flex tw-w-full tw-items-start tw-gap-3 tw-self-stretch">
        {toggles.map((toggle) => {
          const isSelected = toggle.option === WaitlistAdjustmentOption.IncreaseCapacity;
          const toggleContent = (
            <Toggle
              key={toggle.option}
              pressed={isSelected}
              disabled={toggle.disabled}
              className={cn(
                ' tw-box-border tw-flex tw-h-full tw-max-w-[250px] tw-flex-1 tw-flex-shrink-0 tw-flex-col tw-items-start tw-justify-start tw-gap-4 tw-self-stretch tw-rounded-[16px] tw-bg-white  tw-p-4  data-[state=on]:tw-bg-white data-[state=on]:tw-text-accent-foreground',
                isSelected && 'tw-border-[2px]',
                toggle.disabled && 'tw-cursor-not-allowed tw-opacity-40'
              )}
            >
              <div className="tw-flex tw-min-h-[88px] tw-min-w-[218px] tw-items-center tw-justify-center tw-rounded-xl tw-bg-positive-green-light">
                <toggle.icon className="tw-text-positive-foreground" />
              </div>
              <div className="tw-flex tw-w-[218px] tw-flex-col tw-gap-2 tw-text-left">
                <span className="tw-text-sm tw-font-medium tw-text-black">{toggle.title}</span>
                <span className="tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                  {toggle.text}
                </span>
              </div>
            </Toggle>
          );

          if (toggle.disabled) {
            return (
              <Tooltip key={toggle.option}>
                <TooltipTrigger asChild>
                  <div className="tw-flex-1 tw-self-stretch">{toggleContent}</div>
                </TooltipTrigger>
                <TooltipContent
                  side="left"
                  sideOffset={-100}
                  className="tw-mb-[60px] tw-w-[221px] tw-p-2"
                >
                  Please reach out to support@epochapp.com to swap spots between guests.
                </TooltipContent>
              </Tooltip>
            );
          }

          return toggleContent;
        })}
      </div>
      <ModalFooter className="tw-mt-0 tw-flex tw-flex-row tw-justify-end">
        <div className="tw-flex tw-flex-1 tw-items-center">
          <StepBar
            totalSteps={navigation.totalStepNumber}
            currentStep={navigation.currentStepNumber}
          />
        </div>
        <div className="tw-flex tw-flex-row tw-justify-center tw-gap-4">
          <Button
            size="sm"
            onClick={navigation.onClickBack}
            className="tw-flex-1 tw-rounded-xl"
            variant="secondary"
          >
            Back
          </Button>
          <Button
            size="sm"
            className="tw-flex-1 tw-rounded-xl"
            variant="primary"
            onClick={navigation.onClickSubmit}
          >
            Next
            <ArrowRight className="tw-ml-1 tw-h-4 tw-w-4" />
          </Button>
        </div>
      </ModalFooter>
    </>
  );
};
